// src/modules/stock/ProductReportPage.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    query,
    orderBy,
    where
} from 'firebase/firestore';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import {
    FormModal,
    ConfirmModal,
    PageHeader,
    Toast,
    Pagination
} from '../../components/common';
import styles from '../Stock/Logistics.module.css';

// Collection Constants
const PRODUCTS_COLLECTION = 'products';
const REPORT_HISTORY_COLLECTION = 'product-report-history';
const PRODUCT_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-produto'
};
const CATEGORY_COLLECTION = 'parameters/data/product-categories';

// Report Types
const REPORT_TYPES = {
    ALL_PRODUCTS: 'all',
    SINGLE_PRODUCT: 'single'
};

// Stock Status Thresholds
const STOCK_STATUS = {
    NORMAL: 500,
    LOW: 100,
    CRITICAL: 0
};

const ProductReportPage = () => {
    // State
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // Report Configuration State
    const [reportConfig, setReportConfig] = useState({
        reportType: REPORT_TYPES.ALL_PRODUCTS,
        selectedProductId: '',
        includeRawMaterials: true,
        includeQualityCriteria: true,
        includePreparationSteps: true,
        includeFiscalInfo: true,
        includeCostCalculation: true,
        includeStockInfo: true,
        productSituation: 'all',
        minSuggestedPrice: '',
        maxSuggestedPrice: '',
        searchTerm: ''
    });

    // Data States
    const [currentStock, setCurrentStock] = useState([]);
    const [allRawMaterials, setAllRawMaterials] = useState([]);

    // Hooks for data
    const { data: products, loading: loadingProducts } = useFirestore(PRODUCTS_COLLECTION, 'code');
    const { data: productTypes, loading: loadingTypes } = useSubCollection(
        PRODUCT_TYPE_COLLECTION_PATH.parentCollection,
        PRODUCT_TYPE_COLLECTION_PATH.parentId,
        PRODUCT_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );
    const { data: categories, loading: loadingCategories } = useFirestore(CATEGORY_COLLECTION, 'order');

    // Get current user
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    // Load current stock data
    useEffect(() => {
        const fetchStock = async () => {
            try {
                const stockQuery = query(collection(db, 'current-stock'), orderBy('rawMaterialCode'));
                const snapshot = await getDocs(stockQuery);
                const stockItems = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCurrentStock(stockItems);
            } catch (error) {
                console.error("Error loading stock:", error);
            }
        };
        fetchStock();
    }, []);

    // Load raw materials
    useEffect(() => {
        const fetchRawMaterials = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'raw-materials'));
                const materials = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAllRawMaterials(materials);
            } catch (error) {
                console.error("Error loading raw materials:", error);
            }
        };
        fetchRawMaterials();
    }, []);

    // Load report history
    useEffect(() => {
        loadReports();
    }, []);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const loadReports = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, REPORT_HISTORY_COLLECTION), orderBy('generatedAt', 'desc'));
            const snapshot = await getDocs(q);
            const reportsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                generatedAt: doc.data().generatedAt?.toDate() || new Date()
            }));
            setReports(reportsList);
        } catch (error) {
            console.error("Error loading report history:", error);
            addToast('Erro ao carregar histórico', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setIsConfirmOpen(true);
    };

    const deleteReport = async () => {
        if (!reportToDelete) return;

        try {
            await deleteDoc(doc(db, REPORT_HISTORY_COLLECTION, reportToDelete));
            setReports(reports.filter(r => r.id !== reportToDelete));
            addToast('Relatório excluído com sucesso', 'success');
        } catch (error) {
            console.error("Error deleting report:", error);
            addToast('Erro ao excluir relatório', 'error');
        } finally {
            setIsConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    // Helper function to get product stock quantity
    const getProductStock = useCallback((productId, productCode) => {
        const stockItems = currentStock.filter(item =>
            item.rawMaterialId === productId || item.rawMaterialCode === productCode
        );

        const totalQuantity = stockItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const locations = stockItems.map(item => ({
            id: item.locationId,
            description: item.locationDescription,
            quantity: item.quantity
        }));

        return { totalQuantity, locations, items: stockItems };
    }, [currentStock]);

    // Helper function to get stock status label
    const getStockStatus = (quantity) => {
        if (quantity >= STOCK_STATUS.NORMAL) return { label: 'Normal', color: '#28a745', status: 'normal' };
        if (quantity >= STOCK_STATUS.LOW) return { label: 'Baixo', color: '#ffc107', status: 'low' };
        if (quantity > STOCK_STATUS.CRITICAL) return { label: 'Crítico', color: '#dc3545', status: 'critical' };
        return { label: 'Sem Estoque', color: '#6c757d', status: 'zero' };
    };

    // Helper function to calculate product cost
    const calculateProductCost = useCallback((product) => {
        if (!product?.rawMaterials || product.rawMaterials.length === 0) {
            return product?.totalFormulationCost || 0;
        }

        const unitCategoryIds = categories
            ?.filter(c => ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
            .map(c => c.id) || [];

        const isUnitCalculation = (mpId) => {
            const mpData = allRawMaterials.find(mp => mp.id === mpId);
            return mpData?.categoryIds?.some(id => unitCategoryIds.includes(id));
        };

        const getLatestPrice = (mpId, fallbackValue) => {
            const mpData = allRawMaterials.find(mp => mp.id === mpId);
            if (mpData?.suppliers?.[0]) {
                return parseFloat(mpData.suppliers[0].valor) || 0;
            }
            return parseFloat(fallbackValue) || 0;
        };

        const rawMaterialsCost = product.rawMaterials.reduce((acc, mp) => {
            if (!isUnitCalculation(mp.id)) {
                const price = getLatestPrice(mp.id, mp.valor);
                const quantity = parseFloat(mp.quantity) || 0;
                return acc + (price * quantity) / 100;
            }
            return acc;
        }, 0);

        const netWeightVal = parseFloat(product.netWeight) || 0;
        const rawMaterialsTotalCost = rawMaterialsCost * netWeightVal;

        const unitMaterialsCost = product.rawMaterials.reduce((acc, mp) => {
            if (isUnitCalculation(mp.id)) {
                const price = getLatestPrice(mp.id, mp.valor);
                const quantity = parseFloat(mp.quantity) || 0;
                return acc + (price * quantity);
            }
            return acc;
        }, 0);

        const totalCost = rawMaterialsTotalCost + unitMaterialsCost;
        const markup = parseFloat(product.markup) || 1;
        const suggestedPrice = totalCost * markup;

        return { totalCost, suggestedPrice, rawMaterialsCost, unitMaterialsCost };
    }, [allRawMaterials, categories]);

    // Filter products based on report config
    const filteredProducts = useMemo(() => {
        if (!products) return [];

        let filtered = [...products];

        if (reportConfig.productSituation !== 'all') {
            filtered = filtered.filter(p =>
                p.situation?.toLowerCase() === reportConfig.productSituation.toLowerCase()
            );
        }

        if (reportConfig.searchTerm) {
            const term = reportConfig.searchTerm.toLowerCase();
            filtered = filtered.filter(p =>
                p.code?.toLowerCase().includes(term) ||
                p.commercialName?.toLowerCase().includes(term) ||
                p.sigla?.toLowerCase().includes(term)
            );
        }

        if (reportConfig.minSuggestedPrice || reportConfig.maxSuggestedPrice) {
            filtered = filtered.filter(p => {
                const cost = calculateProductCost(p);
                const price = cost.suggestedPrice;

                if (reportConfig.minSuggestedPrice && price < parseFloat(reportConfig.minSuggestedPrice)) return false;
                if (reportConfig.maxSuggestedPrice && price > parseFloat(reportConfig.maxSuggestedPrice)) return false;
                return true;
            });
        }

        filtered.sort((a, b) => (a.code || '').localeCompare(b.code || ''));

        return filtered;
    }, [products, reportConfig, calculateProductCost]);

    // Get selected product data
    const selectedProduct = useMemo(() => {
        if (!reportConfig.selectedProductId || !products) return null;
        return products.find(p => p.id === reportConfig.selectedProductId);
    }, [products, reportConfig.selectedProductId]);

    // Generate single product report data
    const generateSingleProductData = useCallback((product) => {
        const stock = getProductStock(product.id, product.code);
        const cost = calculateProductCost(product);
        const stockStatus = getStockStatus(stock.totalQuantity);

        const typeNames = product.productTypeIds?.map(id =>
            productTypes?.find(t => t.id === id)?.name || ''
        ).filter(Boolean).join(', ') || 'N/A';

        const categoryNames = product.categoryIds?.map(id =>
            categories?.find(c => c.id === id)?.name || ''
        ).filter(Boolean).join(', ') || 'N/A';

        const rawMaterialsData = reportConfig.includeRawMaterials && product.rawMaterials?.length ?
            product.rawMaterials.map((mp, idx) => {
                const mpData = allRawMaterials.find(m => m.id === mp.id);
                return {
                    order: mp.order || idx + 1,
                    fase: mp.fase || '',
                    code: mp.code || mpData?.code || 'N/A',
                    name: mp.inciName || mpData?.inciName || mpData?.commercialName || 'N/A',
                    quantity: mp.quantity || 0,
                    unit: mp.isSemiAcabado ? 'un' : '%',
                    cost: mp.valor || 0,
                    lineTotal: cost.totalCost ? (cost.totalCost * (parseFloat(mp.quantity) || 0) / 100) : 0
                };
            }) : [];

        const qualityCriteriaData = reportConfig.includeQualityCriteria && product.qualityCriteria?.length ?
            product.qualityCriteria.map((qc, idx) => ({
                order: idx + 1,
                analysis: qc.analysis || 'N/A',
                method: qc.method || 'N/A',
                specification: qc.specification || 'N/A',
                control: qc.control || 'N/A'
            })) : [];

        const preparationStepsData = reportConfig.includePreparationSteps && product.preparationSteps?.length ?
            product.preparationSteps.map((step, idx) => ({
                order: step.order || idx + 1,
                description: step.description || ''
            })) : [];

        const locationData = reportConfig.includeStockInfo && stock.locations.length ?
            stock.locations.map(loc => ({
                location: loc.id,
                description: loc.description || 'N/A',
                quantity: loc.quantity
            })) : [];

        return {
            basicInfo: {
                code: product.code || 'N/A',
                sigla: product.sigla || 'N/A',
                commercialName: product.commercialName || 'N/A',
                situation: product.situation || 'N/A',
                types: typeNames,
                categories: categoryNames
            },
            physicalCharacteristics: {
                netWeight: product.netWeight || 0,
                grossWeight: product.grossWeight || 0,
                density: product.density || 0,
                lossIndex: product.lossIndex || 0,
                gramatura: product.gramatura || 0,
                gramaturaUnit: product.gramaturaUnit || 'g',
                shelfLifeMonths: product.shelfLifeMonths || 0
            },
            costCalculation: reportConfig.includeCostCalculation ? {
                rawMaterialsCost: cost.rawMaterialsCost || 0,
                unitMaterialsCost: cost.unitMaterialsCost || 0,
                totalCost: cost.totalCost || 0,
                markup: product.markup || 1,
                suggestedPrice: cost.suggestedPrice || 0
            } : null,
            stockInfo: reportConfig.includeStockInfo ? {
                totalQuantity: stock.totalQuantity,
                status: stockStatus.label,
                statusColor: stockStatus.color,
                locations: locationData,
                locationCount: stock.locations.length
            } : null,
            rawMaterials: rawMaterialsData,
            qualityCriteria: qualityCriteriaData,
            preparationSteps: preparationStepsData,
            fiscalInfo: reportConfig.includeFiscalInfo && product.fiscal ? {
                origemOperacaoComercial: product.fiscal.origemOperacaoComercial || 'N/A',
                origemMercadoriaIcms: product.fiscal.origemMercadoriaIcms || 'N/A',
                ncmId: product.fiscal.ncmId || 'N/A',
                excecaoFiscalIpi: product.fiscal.excecaoFiscalIpi ? 'Sim' : 'Não',
                excecaoFiscalIva: product.fiscal.excecaoFiscalIva ? 'Sim' : 'Não',
                excecaoFiscalPisCofins: product.fiscal.excecaoFiscalPisCofins ? 'Sim' : 'Não',
                valorPautaRs: product.fiscal.valorPautaRs || 0,
                classificacaoTributariaRt: product.fiscal.classificacaoTributariaRt || 'N/A'
            } : null
        };
    }, [getProductStock, calculateProductCost, productTypes, categories, allRawMaterials, reportConfig]);

    // Sheet creation functions
    const createSingleProductCoverSheet = (sheet, product, data, timestamp) => {
        sheet.columns = [
            { key: 'field', width: 30 },
            { key: 'value', width: 50 }
        ];

        sheet.getCell('A1').value = 'RELATÓRIO DE PRODUTO';
        sheet.getCell('A1').font = { size: 18, bold: true };
        sheet.mergeCells('A1:B1');

        sheet.getCell('A3').value = 'Data de Geração:';
        sheet.getCell('B3').value = timestamp.toLocaleString('pt-BR');

        sheet.getCell('A5').value = 'INFORMAÇÕES BÁSICAS';
        sheet.getCell('A5').font = { bold: true, size: 12 };
        sheet.getCell('A5').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
        sheet.getCell('A5').font = { color: { argb: 'FFFFFFFF' } };

        sheet.addRow(['Código:', product.code]);
        sheet.addRow(['Sigla:', product.sigla]);
        sheet.addRow(['Nome Comercial:', product.commercialName]);
        sheet.addRow(['Situação:', product.situation]);
        sheet.addRow(['Tipo(s):', data.basicInfo.types]);
        sheet.addRow(['Categoria(s):', data.basicInfo.categories]);

        sheet.addRow([]);
        sheet.getCell(`A${sheet.rowCount + 1}`).value = 'CARACTERÍSTICAS FÍSICAS';
        sheet.getCell(`A${sheet.rowCount}`).font = { bold: true, size: 12 };
        sheet.getCell(`A${sheet.rowCount}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
        sheet.getCell(`A${sheet.rowCount}`).font = { color: { argb: 'FFFFFFFF' } };

        sheet.addRow(['Peso Líquido (kg):', data.physicalCharacteristics.netWeight]);
        sheet.addRow(['Peso Bruto (kg):', data.physicalCharacteristics.grossWeight]);
        sheet.addRow(['Densidade (g/cm³):', data.physicalCharacteristics.density]);
        sheet.addRow(['Índice de Perda (%):', data.physicalCharacteristics.lossIndex]);
        sheet.addRow(['Gramatura:', `${data.physicalCharacteristics.gramatura} ${data.physicalCharacteristics.gramaturaUnit}`]);
        sheet.addRow(['Validade (meses):', data.physicalCharacteristics.shelfLifeMonths]);

        if (data.costCalculation) {
            sheet.addRow([]);
            sheet.getCell(`A${sheet.rowCount + 1}`).value = 'CUSTOS E PREÇOS';
            sheet.getCell(`A${sheet.rowCount}`).font = { bold: true, size: 12 };
            sheet.getCell(`A${sheet.rowCount}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
            sheet.getCell(`A${sheet.rowCount}`).font = { color: { argb: 'FFFFFFFF' } };

            sheet.addRow(['Custo Matérias-Primas (%):', data.costCalculation.rawMaterialsCost?.toFixed(4)]);
            sheet.addRow(['Custo Materiais Unitários:', data.costCalculation.unitMaterialsCost?.toFixed(4)]);
            sheet.addRow(['Custo Total da Formulação:', data.costCalculation.totalCost?.toFixed(4)]);
            sheet.addRow(['Markup:', data.costCalculation.markup]);
            sheet.addRow(['Preço Sugerido:', `R$ ${data.costCalculation.suggestedPrice?.toFixed(4)}`]);
        }

        if (data.stockInfo) {
            sheet.addRow([]);
            sheet.getCell(`A${sheet.rowCount + 1}`).value = 'INFORMAÇÕES DE ESTOQUE';
            sheet.getCell(`A${sheet.rowCount}`).font = { bold: true, size: 12 };
            sheet.getCell(`A${sheet.rowCount}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
            sheet.getCell(`A${sheet.rowCount}`).font = { color: { argb: 'FFFFFFFF' } };

            sheet.addRow(['Quantidade Total em Estoque:', data.stockInfo.totalQuantity]);
            sheet.addRow(['Status:', data.stockInfo.status]);
            sheet.addRow(['Nº de Localizações:', data.stockInfo.locationCount]);
        }

        if (data.fiscalInfo) {
            sheet.addRow([]);
            sheet.getCell(`A${sheet.rowCount + 1}`).value = 'INFORMAÇÕES FISCAIS';
            sheet.getCell(`A${sheet.rowCount}`).font = { bold: true, size: 12 };
            sheet.getCell(`A${sheet.rowCount}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
            sheet.getCell(`A${sheet.rowCount}`).font = { color: { argb: 'FFFFFFFF' } };

            sheet.addRow(['Origem Operação Comercial:', data.fiscalInfo.origemOperacaoComercial]);
            sheet.addRow(['Origem Mercadoria ICMS:', data.fiscalInfo.origemMercadoriaIcms]);
            sheet.addRow(['NCM:', data.fiscalInfo.ncmId]);
            sheet.addRow(['Exceção Fiscal IPI:', data.fiscalInfo.excecaoFiscalIpi]);
            sheet.addRow(['Exceção Fiscal IVA:', data.fiscalInfo.excecaoFiscalIva]);
            sheet.addRow(['Exceção Fiscal PIS/COFINS:', data.fiscalInfo.excecaoFiscalPisCofins]);
            sheet.addRow(['Valor da Pauta RS:', data.fiscalInfo.valorPautaRs]);
            sheet.addRow(['Classificação Tributária RT:', data.fiscalInfo.classificacaoTributariaRt]);
        }

        sheet.eachRow((row, rowNumber) => {
            row.eachCell((cell) => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });
        });
    };

    const createProductsSummarySheet = (sheet, productsList, timestamp, config) => {
        const columns = [
            { header: 'Código', key: 'code', width: 15 },
            { header: 'Sigla', key: 'sigla', width: 10 },
            { header: 'Nome Comercial', key: 'name', width: 40 },
            { header: 'Situação', key: 'situation', width: 12 },
            { header: 'Tipos', key: 'types', width: 30 },
            { header: 'Categorias', key: 'categories', width: 30 },
            { header: 'Peso Líquido (kg)', key: 'netWeight', width: 15 },
            { header: 'Estoque Total', key: 'stock', width: 15 },
            { header: 'Status Estoque', key: 'stockStatus', width: 15 },
            { header: 'Custo Total (R$)', key: 'totalCost', width: 18 },
            { header: 'Preço Sugerido (R$)', key: 'suggestedPrice', width: 18 },
            { header: 'Validade (meses)', key: 'shelfLife', width: 15 }
        ];

        sheet.columns = columns;

        sheet.getCell('A1').value = `RELATÓRIO DE PRODUTOS - ${timestamp.toLocaleString('pt-BR')}`;
        sheet.getCell('A1').font = { size: 14, bold: true };
        sheet.mergeCells(`A1:${String.fromCharCode(64 + columns.length)}1`);

        let filterRow = 3;
        sheet.getCell(`A${filterRow}`).value = 'Filtros Aplicados:';
        sheet.getCell(`A${filterRow}`).font = { bold: true };

        let filterText = [];
        if (config.productSituation !== 'all') filterText.push(`Situação: ${config.productSituation}`);
        if (config.minSuggestedPrice) filterText.push(`Preço Mín.: R$ ${config.minSuggestedPrice}`);
        if (config.maxSuggestedPrice) filterText.push(`Preço Máx.: R$ ${config.maxSuggestedPrice}`);
        if (config.searchTerm) filterText.push(`Busca: ${config.searchTerm}`);

        sheet.getCell(`B${filterRow}`).value = filterText.join(' | ') || 'Nenhum filtro adicional';
        sheet.mergeCells(`B${filterRow}:${String.fromCharCode(64 + columns.length)}${filterRow}`);

        const rows = [];
        for (const product of productsList) {
            const stock = getProductStock(product.id, product.code);
            const stockStatus = getStockStatus(stock.totalQuantity);
            const cost = calculateProductCost(product);

            const typeNames = product.productTypeIds?.map(id =>
                productTypes?.find(t => t.id === id)?.name || ''
            ).filter(Boolean).join(', ') || 'N/A';

            const categoryNames = product.categoryIds?.map(id =>
                categories?.find(c => c.id === id)?.name || ''
            ).filter(Boolean).join(', ') || 'N/A';

            rows.push({
                code: product.code,
                sigla: product.sigla,
                name: product.commercialName,
                situation: product.situation,
                types: typeNames,
                categories: categoryNames,
                netWeight: product.netWeight || 0,
                stock: stock.totalQuantity,
                stockStatus: stockStatus.label,
                totalCost: cost.totalCost?.toFixed(4) || 0,
                suggestedPrice: cost.suggestedPrice?.toFixed(4) || 0,
                shelfLife: product.shelfLifeMonths || 0
            });
        }

        rows.forEach(row => sheet.addRow(row));

        const headerRow = sheet.getRow(columns.length + 3);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        sheet.eachRow((row) => {
            row.eachCell((cell) => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });
        });
    };

    const createRawMaterialsSheet = (sheet, materials, productName = '') => {
        sheet.columns = [
            { header: 'Ordem', key: 'order', width: 8 },
            { header: 'Fase', key: 'fase', width: 15 },
            { header: 'Código', key: 'code', width: 15 },
            { header: 'INCI Name / Nome', key: 'name', width: 40 },
            { header: 'Quantidade', key: 'quantity', width: 12 },
            { header: 'Unidade', key: 'unit', width: 8 },
            { header: 'Custo Unitário (R$)', key: 'cost', width: 18 },
            { header: 'Total (R$)', key: 'lineTotal', width: 18 }
        ];

        if (productName) {
            sheet.getCell('A1').value = `Matérias-Primas - ${productName}`;
            sheet.getCell('A1').font = { bold: true, size: 12 };
            sheet.mergeCells('A1:H1');
        }

        materials.forEach(m => sheet.addRow(m));

        const headerRow = sheet.getRow(productName ? 3 : 1);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF70AD47' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        });
    };

    const createQualityCriteriaSheet = (sheet, criteria, productName = '') => {
        sheet.columns = [
            { header: 'Ordem', key: 'order', width: 8 },
            { header: 'Análise', key: 'analysis', width: 30 },
            { header: 'Especificação', key: 'specification', width: 40 },
            { header: 'Método', key: 'method', width: 30 },
            { header: 'Controle', key: 'control', width: 15 }
        ];

        if (productName) {
            sheet.getCell('A1').value = `Critérios de Qualidade - ${productName}`;
            sheet.getCell('A1').font = { bold: true, size: 12 };
            sheet.mergeCells('A1:E1');
        }

        criteria.forEach(c => sheet.addRow(c));

        const headerRow = sheet.getRow(productName ? 3 : 1);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFED7D31' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        });
    };

    const createPreparationStepsSheet = (sheet, steps, productName = '') => {
        sheet.columns = [
            { header: 'Ordem', key: 'order', width: 8 },
            { header: 'Descrição da Etapa', key: 'description', width: 80 }
        ];

        if (productName) {
            sheet.getCell('A1').value = `Modo de Preparo - ${productName}`;
            sheet.getCell('A1').font = { bold: true, size: 12 };
            sheet.mergeCells('A1:B1');
        }

        steps.forEach(s => sheet.addRow(s));

        const headerRow = sheet.getRow(productName ? 3 : 1);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF9C27B0' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        });
    };

    const createStockLocationsSheet = (sheet, locations) => {
        sheet.columns = [
            { header: 'Localização', key: 'location', width: 15 },
            { header: 'Descrição', key: 'description', width: 30 },
            { header: 'Quantidade', key: 'quantity', width: 15 }
        ];

        locations.forEach(loc => sheet.addRow(loc));

        const headerRow = sheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        });
    };

    const generateReport = async () => {
        if (!currentUser) {
            addToast('Você precisa estar logado para gerar relatórios', 'error');
            return;
        }

        if (reportConfig.reportType === REPORT_TYPES.SINGLE_PRODUCT && !reportConfig.selectedProductId) {
            addToast('Selecione um produto para gerar o relatório', 'warning');
            return;
        }

        if (reportConfig.reportType === REPORT_TYPES.ALL_PRODUCTS && filteredProducts.length === 0) {
            addToast('Nenhum produto encontrado com os filtros selecionados', 'warning');
            return;
        }

        setGenerating(true);
        addToast('Gerando relatório...', 'info');

        try {
            const timestamp = new Date();
            const timestampStr = timestamp.toISOString().slice(0, 19).replace(/:/g, '-');

            let workbook = new ExcelJS.Workbook();
            let fileName = '';
            let reportData = {};
            let itemCount = 0;

            if (reportConfig.reportType === REPORT_TYPES.SINGLE_PRODUCT && selectedProduct) {
                reportData = generateSingleProductData(selectedProduct);
                fileName = `Relatorio_Produto_${selectedProduct.code}_${timestampStr}.xlsx`;
                itemCount = 1;

                const coverSheet = workbook.addWorksheet('Resumo do Produto');
                createSingleProductCoverSheet(coverSheet, selectedProduct, reportData, timestamp);

                if (reportConfig.includeRawMaterials && reportData.rawMaterials.length > 0) {
                    const rawMaterialsSheet = workbook.addWorksheet('Matérias-Primas');
                    createRawMaterialsSheet(rawMaterialsSheet, reportData.rawMaterials);
                }

                if (reportConfig.includeQualityCriteria && reportData.qualityCriteria.length > 0) {
                    const qualitySheet = workbook.addWorksheet('Critérios de Qualidade');
                    createQualityCriteriaSheet(qualitySheet, reportData.qualityCriteria);
                }

                if (reportConfig.includePreparationSteps && reportData.preparationSteps.length > 0) {
                    const stepsSheet = workbook.addWorksheet('Modo de Preparo');
                    createPreparationStepsSheet(stepsSheet, reportData.preparationSteps);
                }

                if (reportConfig.includeStockInfo && reportData.stockInfo?.locations?.length > 0) {
                    const stockSheet = workbook.addWorksheet('Localizações em Estoque');
                    createStockLocationsSheet(stockSheet, reportData.stockInfo.locations);
                }

            } else {
                fileName = `Relatorio_Produtos_${timestampStr}.xlsx`;
                itemCount = filteredProducts.length;

                const summarySheet = workbook.addWorksheet('Produtos');
                createProductsSummarySheet(summarySheet, filteredProducts, timestamp, reportConfig);

                if (reportConfig.includeRawMaterials || reportConfig.includeQualityCriteria || reportConfig.includePreparationSteps) {
                    for (const product of filteredProducts.slice(0, 20)) {
                        const productData = generateSingleProductData(product);
                        const safeName = (product.code || 'produto').replace(/[\\/*?:"<>|]/g, '_').slice(0, 31);

                        if (reportConfig.includeRawMaterials && productData.rawMaterials.length > 0) {
                            let mpSheet = workbook.getWorksheet(`${safeName}_MPs`);
                            if (!mpSheet) {
                                mpSheet = workbook.addWorksheet(`${safeName}_MPs`);
                                createRawMaterialsSheet(mpSheet, productData.rawMaterials, product.commercialName);
                            }
                        }

                        if (reportConfig.includeQualityCriteria && productData.qualityCriteria.length > 0) {
                            let qcSheet = workbook.getWorksheet(`${safeName}_Qualidade`);
                            if (!qcSheet) {
                                qcSheet = workbook.addWorksheet(`${safeName}_Qualidade`);
                                createQualityCriteriaSheet(qcSheet, productData.qualityCriteria, product.commercialName);
                            }
                        }
                    }
                }
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, fileName);

            const historyData = {
                fileName,
                generatedAt: serverTimestamp(),
                generatedBy: currentUser.email || currentUser.displayName || currentUser.uid,
                generatedByEmail: currentUser.email,
                generatedByName: currentUser.displayName || '',
                itemCount,
                reportType: reportConfig.reportType,
                config: {
                    reportType: reportConfig.reportType,
                    selectedProductId: reportConfig.selectedProductId,
                    selectedProductName: selectedProduct?.commercialName || null,
                    selectedProductCode: selectedProduct?.code || null,
                    includeRawMaterials: reportConfig.includeRawMaterials,
                    includeQualityCriteria: reportConfig.includeQualityCriteria,
                    includePreparationSteps: reportConfig.includePreparationSteps,
                    includeFiscalInfo: reportConfig.includeFiscalInfo,
                    includeCostCalculation: reportConfig.includeCostCalculation,
                    includeStockInfo: reportConfig.includeStockInfo,
                    productSituation: reportConfig.productSituation,
                    minSuggestedPrice: reportConfig.minSuggestedPrice || null,
                    maxSuggestedPrice: reportConfig.maxSuggestedPrice || null,
                    searchTerm: reportConfig.searchTerm || null
                }
            };

            const docRef = await addDoc(collection(db, REPORT_HISTORY_COLLECTION), historyData);

            setReports([
                { id: docRef.id, ...historyData, generatedAt: new Date() },
                ...reports
            ]);

            addToast(`Relatório "${fileName}" gerado com sucesso!`, 'success');
            setIsModalOpen(false);

        } catch (error) {
            console.error('Error generating report:', error);
            addToast(`Erro ao gerar relatório: ${error.message}`, 'error');
        } finally {
            setGenerating(false);
        }
    };

    // Reset configuration
    const resetConfig = () => {
        setReportConfig({
            reportType: REPORT_TYPES.ALL_PRODUCTS,
            selectedProductId: '',
            includeRawMaterials: true,
            includeQualityCriteria: true,
            includePreparationSteps: true,
            includeFiscalInfo: true,
            includeCostCalculation: true,
            includeStockInfo: true,
            productSituation: 'all',
            minSuggestedPrice: '',
            maxSuggestedPrice: '',
            searchTerm: ''
        });
    };

    const openModal = () => {
        resetConfig();
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        resetConfig();
    };

    const handleConfigChange = (key, value) => {
        setReportConfig(prev => ({ ...prev, [key]: value }));
        if (key === 'reportType' && value === REPORT_TYPES.ALL_PRODUCTS) {
            setReportConfig(prev => ({ ...prev, selectedProductId: '' }));
        }
    };

    const formatDate = (date) => {
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getReportTypeLabel = (type) => {
        return type === REPORT_TYPES.SINGLE_PRODUCT ? 'Produto Específico' : 'Todos os Produtos';
    };

    const getConfigDescription = (config) => {
        const parts = [];

        if (config.reportType === REPORT_TYPES.SINGLE_PRODUCT && config.selectedProductName) {
            parts.push(`Produto: ${config.selectedProductCode} - ${config.selectedProductName}`);
        }

        if (config.productSituation !== 'all') {
            parts.push(`Situação: ${config.productSituation === 'active' ? 'Ativo' : 'Inativo'}`);
        }

        if (config.minSuggestedPrice) parts.push(`Preço Mín: R$ ${config.minSuggestedPrice}`);
        if (config.maxSuggestedPrice) parts.push(`Preço Máx: R$ ${config.maxSuggestedPrice}`);
        if (config.searchTerm) parts.push(`Busca: ${config.searchTerm}`);

        const sections = [];
        if (config.includeRawMaterials) sections.push('MPs');
        if (config.includeQualityCriteria) sections.push('Qualidade');
        if (config.includePreparationSteps) sections.push('Preparo');
        if (config.includeFiscalInfo) sections.push('Fiscal');
        if (config.includeCostCalculation) sections.push('Custos');
        if (config.includeStockInfo) sections.push('Estoque');

        if (sections.length > 0 && sections.length < 6) {
            parts.push(`Inclui: ${sections.join(', ')}`);
        }

        return parts.length > 0 ? parts.join(' | ') : 'Todos os produtos e seções';
    };

    const isLoading = loadingProducts || loadingTypes || loadingCategories;

    // Render filter form content for modal
    const renderFilterForm = () => (
        <>
            <div className={styles.filterRow}>
                <div className={styles.inputGroup}>
                    <label>Tipo de Relatório</label>
                    <select
                        value={reportConfig.reportType}
                        onChange={(e) => handleConfigChange('reportType', e.target.value)}
                        className={styles.select}
                    >
                        <option value={REPORT_TYPES.ALL_PRODUCTS}>Todos os Produtos</option>
                        <option value={REPORT_TYPES.SINGLE_PRODUCT}>Produto Específico</option>
                    </select>
                </div>
            </div>

            {reportConfig.reportType === REPORT_TYPES.SINGLE_PRODUCT && (
                <div className={styles.filterRow}>
                    <div className={styles.inputGroup}>
                        <label>Selecione o Produto</label>
                        <select
                            value={reportConfig.selectedProductId}
                            onChange={(e) => handleConfigChange('selectedProductId', e.target.value)}
                            className={styles.select}
                            disabled={isLoading}
                        >
                            <option value="">-- Selecione um produto --</option>
                            {products?.map(product => (
                                <option key={product.id} value={product.id}>
                                    {product.code} - {product.commercialName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {reportConfig.reportType === REPORT_TYPES.ALL_PRODUCTS && (
                <>
                    <div className={styles.filterRow}>
                        <div className={styles.inputGroup}>
                            <label>Situação do Produto</label>
                            <select
                                value={reportConfig.productSituation}
                                onChange={(e) => handleConfigChange('productSituation', e.target.value)}
                                className={styles.select}
                            >
                                <option value="all">Todos</option>
                                <option value="active">Ativos</option>
                                <option value="inactive">Inativos</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.filterRow}>
                        <div className={styles.inputGroup}>
                            <label>Preço Sugerido Mínimo (R$)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Ex: 10.00"
                                value={reportConfig.minSuggestedPrice}
                                onChange={(e) => handleConfigChange('minSuggestedPrice', e.target.value)}
                                className={styles.input}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>Preço Sugerido Máximo (R$)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Ex: 100.00"
                                value={reportConfig.maxSuggestedPrice}
                                onChange={(e) => handleConfigChange('maxSuggestedPrice', e.target.value)}
                                className={styles.input}
                            />
                        </div>
                    </div>

                    <div className={styles.filterRow}>
                        <div className={styles.inputGroup}>
                            <label>Busca (código, nome ou sigla)</label>
                            <input
                                type="text"
                                placeholder="Digite para buscar..."
                                value={reportConfig.searchTerm}
                                onChange={(e) => handleConfigChange('searchTerm', e.target.value)}
                                className={styles.input}
                            />
                        </div>
                    </div>
                </>
            )}

            <div className={styles.filterRow}>
                <div className={styles.inputGroup}>
                    <label style={{ marginBottom: '12px', display: 'block' }}>Seções a Incluir no Relatório</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={reportConfig.includeRawMaterials}
                                onChange={(e) => handleConfigChange('includeRawMaterials', e.target.checked)}
                            />
                            Matérias-Primas
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={reportConfig.includeQualityCriteria}
                                onChange={(e) => handleConfigChange('includeQualityCriteria', e.target.checked)}
                            />
                            Critérios de Qualidade
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={reportConfig.includePreparationSteps}
                                onChange={(e) => handleConfigChange('includePreparationSteps', e.target.checked)}
                            />
                            Modo de Preparo
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={reportConfig.includeFiscalInfo}
                                onChange={(e) => handleConfigChange('includeFiscalInfo', e.target.checked)}
                            />
                            Informações Fiscais
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={reportConfig.includeCostCalculation}
                                onChange={(e) => handleConfigChange('includeCostCalculation', e.target.checked)}
                            />
                            Cálculo de Custos
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={reportConfig.includeStockInfo}
                                onChange={(e) => handleConfigChange('includeStockInfo', e.target.checked)}
                            />
                            Informações de Estoque
                        </label>
                    </div>
                </div>
            </div>

            {reportConfig.reportType === REPORT_TYPES.ALL_PRODUCTS && filteredProducts.length > 0 && (
                <div className={styles.previewInfo} style={{ marginTop: '16px' }}>
                    📊 {filteredProducts.length} produto(s) serão incluídos no relatório
                </div>
            )}

            {reportConfig.reportType === REPORT_TYPES.SINGLE_PRODUCT && selectedProduct && (
                <div className={styles.previewInfo} style={{ marginTop: '16px' }}>
                    📄 Produto selecionado: {selectedProduct.code} - {selectedProduct.commercialName}
                </div>
            )}
        </>
    );

    return (
        <div className={styles.pageContainer}>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={deleteReport}
                title="Excluir Relatório"
                message="Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita."
                confirmText="Excluir"
                danger
            />

            {/* Filter Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={generateReport}
                title="Configurar Relatório de Produtos"
                subtitle="Selecione as opções desejadas para o relatório"
                isLoading={generating}
                submitText="Gerar Relatório"
                cancelText="Cancelar"
                maxWidth="800px"
            >
                {renderFilterForm()}
                <div className={styles.modalActions} style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                    <button type="button" onClick={resetConfig} className={styles.cancelButton}>
                        Limpar Filtros
                    </button>
                </div>
            </FormModal>

            {/* Main Header */}
            <PageHeader
                title="Relatório de Produtos"
                actionText="Novo Relatório"
                onAction={openModal}
                disabled={!currentUser || isLoading}
            />

            {/* Report History Section */}
            <div className={styles.tableCard}>
                <h3 className={styles.tableTitle}>Histórico de Relatórios Gerados</h3>
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '160px' }}>Data/Hora</th>
                                <th>Nome do Arquivo</th>
                                <th style={{ width: '200px' }}>Gerado por</th>
                                <th style={{ width: '80px' }}>Itens</th>
                                <th style={{ width: '120px' }}>Tipo</th>
                                <th style={{ width: '250px' }}>Configuração</th>
                                <th style={{ width: '80px' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className={styles.loadingCell}>
                                        <div className={styles.spinner} style={{ margin: '0 auto' }}></div>
                                        <span style={{ marginTop: '8px', display: 'block' }}>Carregando histórico...</span>
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className={styles.emptyTable}>
                                        Nenhum relatório gerado ainda. Clique em "Novo Relatório" para começar.
                                    </td>
                                </tr>
                            ) : (
                                reports.map((report) => (
                                    <tr key={report.id} className={styles.tableRow}>
                                        <td>{formatDate(report.generatedAt)}</td>
                                        <td>
                                            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                                {report.fileName}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ fontWeight: 500 }}>
                                                    {report.generatedByName || report.generatedBy?.split('@')[0] || 'Usuário'}
                                                </span>
                                                {report.generatedByEmail && (
                                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                                        {report.generatedByEmail}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{report.itemCount}</td>
                                        <td style={{ fontSize: '12px' }}>
                                            {getReportTypeLabel(report.reportType)}
                                        </td>
                                        <td style={{ fontSize: '12px' }}>
                                            {getConfigDescription(report.config || {})}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleDeleteClick(report.id)}
                                                className={styles.deleteButton}
                                                style={{ padding: '4px 12px', fontSize: '12px' }}
                                            >
                                                Excluir
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProductReportPage;